import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Search, X, Calendar, Flag, User, Tag, BarChart, ChartGantt, KanbanSquare, LayoutDashboard, FileText, FileBarChart } from 'lucide-react';
import { useProjectData } from '@/features/projects/ProjectDataContext';
import { useMultiProject } from '@/features/projects/MultiProjectContext';
import { useTheme, cn } from '@/ui/theme/ThemeContext';
import { getNodeAssignees, getNodeAssigneeDisplayNames, isAgentAssigneeId } from '@/shared/utils/projectDataUtils';
import { useSettings } from '@/features/settings/contexts/SettingsContext';
import { usePresenceTransition } from '@/shared/hooks/usePresenceTransition';
import type { ProjectNode } from '@/shared/types/projectData';
import { serverDateTimeMs } from '@/shared/utils/serverTime';
import { useAuth } from '@/features/auth/AuthContext';
import { filterNodesByPermissions } from '@/features/projects/helpers/projectDataFilters';
import { shouldRedactAgentAssigneesForRole } from '@/features/projects/helpers/agentAssigneeRedaction';
import { keyboardManager, useModalEscape } from '@/shared/services/KeyboardEventManager';
import { normalizeKanbanStatus } from '@/shared/utils/kanbanState';

interface SearchPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTask?: (taskId: string) => void;
  onNavigateToView?: (viewName: string, taskId?: string, node?: ProjectNode) => void;
}

const stripMarkdownForPreview = (value: string): string =>
  value
    .replace(/!\[[^\]]*\]\([^)]+\)/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[`*_>#~-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const MAX_VISIBLE_RESULTS = 24;

const SEARCH_PANEL_MODAL_ID = 'search-panel';
// Above the App shell's own search entry (200) and below the task panel (250) that a
// result can open on top, so the Tab trap stands down while the task panel is up.
const SEARCH_PANEL_ESCAPE_PRIORITY = 210;

const FOCUSABLE_ELEMENT_SELECTOR = [
  'a[href]:not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"])',
  'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

// Same effective status as the board filter (projectDataFilters.ts): completionState wins
// over the legacy status field, and a node with neither has no status at all.
const getEffectiveStatus = (node: ProjectNode): string | undefined => {
  const rawStatus = node.completionState || node.status;
  return rawStatus ? normalizeKanbanStatus(rawStatus) : undefined;
};

export const SearchPanel: React.FC<SearchPanelProps> = ({ isOpen, onClose, onSelectTask, onNavigateToView }) => {
  const { projectData } = useProjectData();
  const { aggregatedNodes, visibleProjects, getAssigneeDisplayName } = useMultiProject();
  const { theme } = useTheme();
  const { t } = useSettings();
  const { user, hasPermission } = useAuth();
  const { shouldRender, isActive } = usePresenceTransition(isOpen, { duration: 200 });
  const shouldRedactAgentAssignees = shouldRedactAgentAssigneesForRole(user?.role);
  const dialogRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    types: [] as string[],
    statuses: [] as string[],
    priorities: [] as string[],
    assignees: [] as string[],
  });

  // Capture the opener before commit so the search input's focus does not replace it.
  if (isOpen && !wasOpenRef.current && typeof document !== 'undefined') {
    returnFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
  }
  wasOpenRef.current = isOpen;

  useModalEscape(SEARCH_PANEL_MODAL_ID, onClose, {
    enabled: isOpen,
    priority: SEARCH_PANEL_ESCAPE_PRIORITY,
  });

  const searchableNodes = useMemo(() => {
    const visibleIds = new Set(visibleProjects.map(project => project.projectId));
    const source = Array.isArray(aggregatedNodes)
      ? aggregatedNodes
      : projectData?.projectId && visibleIds.has(projectData.projectId) ? projectData.nodes : [];
    const nodes = source.filter(node => {
      const flags = node as ProjectNode & { _deleted?: boolean; isDeleted?: boolean };
      const projectId = node.projectId || projectData?.projectId;
      return Boolean(projectId && visibleIds.has(projectId)) && !flags._deleted && !flags.isDeleted;
    });
    if (!user) return [];
    return user.role === 'admin' ? nodes : filterNodesByPermissions(nodes, hasPermission);
  }, [aggregatedNodes, projectData, visibleProjects, user, hasPermission]);

  const projectNameById = useMemo(() => {
    const names = new Map<string, string>();
    visibleProjects.forEach(project => {
      if (project.projectId) {
        names.set(project.projectId, project.projectName || project.projectId);
      }
    });
    if (projectData?.projectId && projectData?.projectName && !names.has(projectData.projectId)) {
      names.set(projectData.projectId, projectData.projectName);
    }
    return names;
  }, [projectData?.projectId, projectData?.projectName, visibleProjects]);

  const getVisibleAssigneeLabels = React.useCallback((node: ProjectNode): string[] => {
    const assignees = getNodeAssignees(node);
    return getNodeAssigneeDisplayNames(node, getAssigneeDisplayName)
      .filter((_label, index) => !shouldRedactAgentAssignees || !isAgentAssigneeId(assignees[index]));
  }, [getAssigneeDisplayName, shouldRedactAgentAssignees]);

  // Get unique values for filters
  const filterOptions = useMemo(() => {
    const assignees = new Set<string>();
    
    searchableNodes.forEach(node => {
      getVisibleAssigneeLabels(node).forEach((assignee: string) => assignees.add(assignee));
    });

    return {
      types: ['task', 'milestone', 'group'],
      statuses: ['todo', 'in-progress', 'done'],
      priorities: ['low', 'medium', 'high', 'critical'],
      assignees: Array.from(assignees),
    };
  }, [getVisibleAssigneeLabels, searchableNodes]);

  const isMultiProjectSearch = useMemo(() => {
    const projectIds = new Set<string>();
    searchableNodes.forEach(node => {
      if (node.projectId) {
        projectIds.add(node.projectId);
      }
    });
    return projectIds.size > 1;
  }, [searchableNodes]);

  // Search results
  const searchResults = useMemo(() => {
    const query = searchQuery.toLowerCase();

    const matches = searchableNodes.filter(node => {
      // Text search
      const matchesQuery = !query || 
        node.title.toLowerCase().includes(query) ||
        (node.description && node.description.toLowerCase().includes(query)) ||
        getVisibleAssigneeLabels(node).some(label => label.toLowerCase().includes(query)) ||
        (node.tags && node.tags.some(tag => tag.toLowerCase().includes(query)));

      // Type filter
      const matchesType = filters.types.length === 0 || filters.types.includes(node.type);

      // Status filter
      const effectiveStatus = getEffectiveStatus(node);
      const matchesStatus = filters.statuses.length === 0 || 
        (effectiveStatus && filters.statuses.includes(effectiveStatus));

      // Priority filter
      const matchesPriority = filters.priorities.length === 0 || 
        (node.priority && filters.priorities.includes(node.priority));

      // Assignee filter
      const matchesAssignee = filters.assignees.length === 0 || 
        getVisibleAssigneeLabels(node).some((assignee: string) => filters.assignees.includes(assignee));

      return matchesQuery && matchesType && matchesStatus && matchesPriority && matchesAssignee;
    });

    return matches
      .map(node => {
        const title = node.title.toLowerCase();
        const description = (node.description || '').toLowerCase();
        const tags = (node.tags || []).join(' ').toLowerCase();
        let score = 0;

        if (query) {
          if (title === query) score += 120;
          else if (title.startsWith(query)) score += 80;
          else if (title.includes(query)) score += 50;
          if (description.includes(query)) score += 20;
          if (tags.includes(query)) score += 10;
        }

        const updatedAt = serverDateTimeMs(node.updatedAt || node.createdAt || 0);
        return { node, score, updatedAt };
      })
      .sort((left, right) => {
        if (right.score !== left.score) {
          return right.score - left.score;
        }
        return right.updatedAt - left.updatedAt;
      })
      .map(entry => entry.node);
  }, [getVisibleAssigneeLabels, searchQuery, filters, searchableNodes]);

  const visibleResults = useMemo(
    () => searchResults.slice(0, MAX_VISIBLE_RESULTS),
    [searchResults]
  );

  // Focus search input when panel opens and handle ESC key
  useEffect(() => {
    if (isOpen && shouldRender) {
      const input = document.getElementById('search-input');
      if (input) {
        (input as HTMLInputElement).focus();
      }
    }
  }, [isOpen, shouldRender]);

  // Trap Tab inside the dialog while it is the top modal, and return focus to the opener on close.
  useEffect(() => {
    if (!isOpen || !shouldRender) return undefined;

    const dialog = dialogRef.current;
    const activeElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!returnFocusRef.current && activeElement && dialog && !dialog.contains(activeElement)) {
      returnFocusRef.current = activeElement;
    }

    const handleTabKey = (event: KeyboardEvent) => {
      if (
        event.key !== 'Tab'
        || event.defaultPrevented
        || !keyboardManager.isModalOnTop(SEARCH_PANEL_MODAL_ID)
      ) return;

      const currentDialog = dialogRef.current;
      if (!currentDialog) return;

      const focusableElements = Array.from(currentDialog.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENT_SELECTOR));
      const firstElement = focusableElements[0] ?? currentDialog;
      const lastElement = focusableElements[focusableElements.length - 1] ?? currentDialog;
      const currentActiveElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;

      if (!currentActiveElement || !currentDialog.contains(currentActiveElement) || focusableElements.length === 0) {
        event.preventDefault();
        (event.shiftKey ? lastElement : firstElement).focus();
        return;
      }

      if (event.shiftKey && currentActiveElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && currentActiveElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => {
      document.removeEventListener('keydown', handleTabKey);
      const returnTarget = returnFocusRef.current;
      returnFocusRef.current = null;
      if (returnTarget?.isConnected) {
        returnTarget.focus();
      }
    };
  }, [isOpen, shouldRender]);

  if (!shouldRender) return null;

  const toggleFilter = (filterType: keyof typeof filters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType].includes(value)
        ? prev[filterType].filter(v => v !== value)
        : [...prev[filterType], value]
    }));
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'milestone': return <span className="block w-3 h-3 rotate-45 bg-purple-500" />;
      case 'group': return <span className="block w-3 h-3 rounded bg-gray-500" />;
      default: return <span className="block w-3 h-3 rounded-full bg-zinc-500 dark:bg-zinc-400" />;
    }
  };

  const getRelevantViews = () => {
    // All items get the same view options in the same order
    const standardViews = ['kanban', 'gantt', 'calendar', 'docs', 'reports'];
    return standardViews.filter((viewName) => hasPermission(viewName, 'access'));
  };

  const getViewLabel = (viewName: string) => {
    switch (viewName) {
      case 'gantt': return t('Gantt Chart');
      case 'kanban': return t('Kanban Board');
      case 'calendar': return t('Calendar');
      case 'dashboard': return t('Dashboard');
      case 'docs': return t('Docs');
      case 'reports': return t('Reports');
      default: return t('View');
    }
  };

  const getViewIcon = (viewName: string) => {
    switch (viewName) {
      case 'gantt': return <ChartGantt className="w-4 h-4" />;
      case 'kanban': return <KanbanSquare className="w-4 h-4" />;
      case 'calendar': return <Calendar className="w-4 h-4" />;
      case 'dashboard': return <LayoutDashboard className="w-4 h-4" />;
      case 'docs': return <FileText className="w-4 h-4" />;
      case 'reports': return <FileBarChart className="w-4 h-4" />;
      default: return <BarChart className="w-4 h-4" />;
    }
  };

  return (
    <div
      className={cn(
        'fixed inset-0 z-[9999] flex items-start justify-center bg-black/50 pt-20 backdrop-blur-sm transition-opacity duration-200 ease-out motion-reduce:transition-none',
        isActive ? 'opacity-100' : 'opacity-0',
      )}
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('Search')}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
        className={cn(
        "flex max-h-[80vh] w-full max-w-3xl flex-col rounded-lg shadow-2xl outline-none transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none",
        isActive ? 'translate-y-0 scale-100 opacity-100' : '-translate-y-2 scale-[0.985] opacity-0',
        theme.bgPrimary,
        theme.borderPrimary,
        "border"
      )}
      >
        {/* Header */}
        <div className={cn("flex items-center gap-3 p-4 border-b", theme.borderPrimary)}>
          <Search className={cn("w-5 h-5", theme.textSecondary)} />
          <input
            id="search-input"
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('Search tasks, milestones, and groups...')}
            className={cn(
              "flex-1 bg-transparent outline-none text-lg",
              theme.textPrimary,
              "placeholder:text-slate-500 dark:placeholder:text-gray-400"
            )}
          />
          <button
            onClick={onClose}
            aria-label={t('Close')}
            className={cn("p-1 rounded transition-colors", theme.hover)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filters */}
        <div className={cn("p-4 border-b space-y-2", theme.borderPrimary)}>
          {/* Type filters */}
          <div className="flex items-center gap-2">
            <span className={cn("text-sm w-20", theme.textSecondary)}>{t('Type')}:</span>
            <div className="flex gap-2">
              {filterOptions.types.map(type => (
                <button
                  key={type}
                  onClick={() => toggleFilter('types', type)}
                  aria-pressed={filters.types.includes(type)}
                  className={cn(
                    "px-3 py-1 rounded-md text-sm transition-colors",
                    filters.types.includes(type)
                      ? cn(theme.controlActive, theme.selectionRing)
                      : cn(theme.bgSecondary, theme.textSecondary, theme.surfaceHover)
                  )}
                >
                  {t(type)}
                </button>
              ))}
            </div>
          </div>

          {/* Status filters */}
          <div className="flex items-center gap-2">
            <span className={cn("text-sm w-20", theme.textSecondary)}>{t('Status')}:</span>
            <div className="flex gap-2">
              {filterOptions.statuses.map(status => (
                <button
                  key={status}
                  onClick={() => toggleFilter('statuses', status)}
                  aria-pressed={filters.statuses.includes(status)}
                  className={cn(
                    "px-3 py-1 rounded-md text-sm transition-colors",
                    filters.statuses.includes(status)
                      ? cn(theme.controlActive, theme.selectionRing)
                      : cn(theme.bgSecondary, theme.textSecondary, theme.surfaceHover)
                  )}
                >
                  {status === 'in-progress' ? t('In Progress') : t(status)}
                </button>
              ))}
            </div>
          </div>

          {/* Priority filters */}
          <div className="flex items-center gap-2">
            <span className={cn("text-sm w-20", theme.textSecondary)}>{t('Priority')}:</span>
            <div className="flex gap-2">
              {filterOptions.priorities.map(priority => (
                <button
                  key={priority}
                  onClick={() => toggleFilter('priorities', priority)}
                  aria-pressed={filters.priorities.includes(priority)}
                  className={cn(
                    "px-3 py-1 rounded-md text-sm transition-colors",
                    filters.priorities.includes(priority)
                      ? cn(theme.controlActive, theme.selectionRing)
                      : cn(theme.bgSecondary, theme.textSecondary, theme.surfaceHover)
                  )}
                >
                  {t(priority)}
                </button>
              ))}
            </div>
          </div>

          {filterOptions.assignees.length > 0 && (
            <div className="flex items-center gap-2">
              <span className={cn("text-sm w-20", theme.textSecondary)}>{t('Assignees')}:</span>
              <div className="flex gap-2 flex-wrap">
                {filterOptions.assignees.map(assignee => (
                  <button
                    key={assignee}
                    onClick={() => toggleFilter('assignees', assignee)}
                    aria-pressed={filters.assignees.includes(assignee)}
                    className={cn(
                      "px-3 py-1 rounded-md text-sm transition-colors",
                      filters.assignees.includes(assignee)
                        ? cn(theme.controlActive, theme.selectionRing)
                        : cn(theme.bgSecondary, theme.textSecondary, theme.surfaceHover)
                    )}
                  >
                    {assignee}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-4">
          {searchResults.length === 0 ? (
            <div className={cn("text-center py-12", theme.textSecondary)}>
              {searchQuery || Object.values(filters).some(f => f.length > 0)
                ? t('No results found')
                : t('Start typing to search...')}
            </div>
          ) : (
            <div className="space-y-2">
              {visibleResults.map(node => {
                const relevantViews = getRelevantViews();
                const effectiveStatus = getEffectiveStatus(node);
                const descriptionPreview = stripMarkdownForPreview(String(
                  node.description ||
                  (node as unknown as Record<string, unknown>).content ||
                  ''
                ));
                return (
                  <div
                    key={node.id}
                    data-testid={`search-result-${node.id}`}
                    data-search-result-node-id={node.id}
                    data-search-result-title={node.title}
                    className={cn(
                      "rounded-lg transition-colors",
                      theme.bgSecondary
                    )}
                  >
                    {/* Main content area: a real button so Enter/Space open the details */}
                    <button
                      type="button"
                      onClick={() => {
                        onSelectTask?.(node.id);
                        // Keep search open - edit modal will appear on top
                      }}
                      className={cn(
                        "block w-full rounded-t-lg p-3 text-left cursor-pointer transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-zinc-400/70",
                        theme.hover,
                        "hover:bg-opacity-80"
                      )}
                    >
                      <span className="flex items-start gap-3">
                        <span className="mt-1">{getTypeIcon(node.type)}</span>
                        <span className="flex-1">
                          <span className={cn("block font-medium", theme.textPrimary)}>{node.title}</span>
                          {descriptionPreview && (
                            <span className={cn("text-sm line-clamp-1 mt-1", theme.textSecondary)}>
                              {descriptionPreview}
                            </span>
                          )}
                          <span className={cn("flex items-center gap-4 mt-2 text-xs", theme.textSecondary)}>
                            {isMultiProjectSearch && node.projectId && (
                              <span className="flex items-center gap-1">
                                <FileText className="w-3 h-3" />
                                {projectNameById.get(node.projectId) || node.projectId}
                              </span>
                            )}
                            {effectiveStatus && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {t(effectiveStatus)}
                              </span>
                            )}
                            {node.priority && (
                              <span className="flex items-center gap-1">
                                <Flag className="w-3 h-3" />
                                {t(node.priority)}
                              </span>
                            )}
                            {getVisibleAssigneeLabels(node).length > 0 && (
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {getVisibleAssigneeLabels(node).join(', ')}
                              </span>
                            )}
                            {node.tags && node.tags.length > 0 && (
                              <span className="flex items-center gap-1">
                                <Tag className="w-3 h-3" />
                                {node.tags.join(', ')}
                              </span>
                            )}
                          </span>
                        </span>
                      </span>
                    </button>
                    
                    {/* Navigation buttons */}
                    <div className={cn("px-3 pb-3 border-t", theme.borderPrimary)}>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={cn("text-xs", theme.textSecondary)}>{t('Go to')}:</span>
                        <div className="flex items-center gap-1 flex-wrap">
                          {relevantViews.map(viewName => (
                            <button
                              key={viewName}
                              data-testid={`search-result-navigate-${viewName}-${node.id}`}
                              data-search-result-node-id={node.id}
                              data-search-result-view={viewName}
                              onClick={(e) => {
                                e.stopPropagation();
                                onNavigateToView?.(viewName, node.id, node);
                                onClose();
                              }}
                              className={cn(
                                "flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors",
                                theme.bgTertiary,
                                theme.textSecondary,
                                theme.hover
                              )}
                            >
                              {getViewIcon(viewName)}
                              {getViewLabel(viewName)}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={cn("p-4 border-t text-sm text-center", theme.borderPrimary, theme.textSecondary)}>
          {searchResults.length > MAX_VISIBLE_RESULTS
            ? t('Showing {{visible}} of {{total}} results', {
                visible: MAX_VISIBLE_RESULTS,
                total: searchResults.length,
              })
            : t('{{count}} results found', { count: searchResults.length })}
        </div>
      </div>
    </div>
  );
};
